class HeaderComponent extends HTMLElement {
  constructor() {
    super();
    this.headerHeight = 0;
    this.mobileHeaderHeight = 0;
  }
  
  connectedCallback() { 
    this.initHeadspace();
    this.initNestedNavHover();
    this.initSubMenuFocus();
    this.initNestedSubMenuFocus();
    this.handleResize();
    window.addEventListener("resize", this.handleResize.bind(this), { passive: true });
    // Re-measure after layout settles (sub-menus, fonts, etc.)
    setTimeout(() => this.updateHeaderDimensions(), 100);
  }

  initHeadspace() {
    whenReady("Headspace", () => {

      if(this.id === "fixed-header") {
        return;
      }

      if(location.pathname.includes("/search")) {
        return;
      }

        const headspace = new Headspace(this, {
          startOffset: this.headerHeight,
          tolerance: 15,
          showAtBottom: false,
          classNames: {
            base: "headspace",
            fixed: "headspace--fixed",
            hidden: "headspace--hidden",
          },
        }); 

        const handleStickyState = (addSticky, addUnstuck) => {
          document.body.classList.toggle("header-stuck", addSticky);
          document.body.classList.toggle("header-unstuck", addUnstuck);
        };
  
        new ClassWatcher(
          this,
          "swiped-up",
          () => handleStickyState(false, true),
          () => handleStickyState(true, false)
        );
  
        new ClassWatcher(
          this,
          "swiped-down",
          () => handleStickyState(true, false),
          () => handleStickyState(false, false)
        );

    });
  }

  initNestedNavHover() {
    const navItems = this.querySelectorAll('.header__nav-list > li, .top-bar-left > li');
    
    const hideAllActiveNestedNavs = () => {
      this.querySelectorAll('.header__nav-list-nested.active').forEach(nav => {
        nav.classList.remove('active');
      });
    };
    
    navItems.forEach(item => {
      const nestedNav = item.querySelector('.header__nav-list-nested');
      
      if (nestedNav) {
        item.addEventListener('mouseenter', () => {
          hideAllActiveNestedNavs();
          nestedNav.classList.add('active');
        });
        
        nestedNav.addEventListener('mouseenter', () => {
          nestedNav.classList.add('active');
        });
        
        nestedNav.addEventListener('mouseleave', () => {
          nestedNav.classList.remove('active');
        });
      } else {
        item.addEventListener('mouseenter', () => {
          hideAllActiveNestedNavs();
        });
      }
    });
  }

  initSubMenuFocus() {
    const mainNavs = this.querySelectorAll('#navigation .main-nav, #masthead #navigation .main-nav, #masthead .navigation .main-nav, #fixed-header .main-nav, #fixed-header .fixed-navigation .main-nav, .main-nav');
    if (!mainNavs || mainNavs.length === 0) {
      return;
    }

    mainNavs.forEach(mainNav => {
      const navItems = mainNav.querySelectorAll('li.has-children');
      if (navItems.length > 0) {
        this.setupSubMenuFocus(navItems);
      }
    });
  }

  setupSubMenuFocus(navItems) {
    // Track which item currently has an open sub-menu
    let currentOpenSubMenu = null;
    let currentOpenItem = null;
    
    navItems.forEach(item => {
      const subMenu = item.querySelector('.sub-menu');
      // Find direct child anchor tag (prefer direct child, fallback to any anchor)
      let parentLink = Array.from(item.children).find(child => child.tagName === 'A');
      if (!parentLink) {
        parentLink = item.querySelector('a.title, a');
      }
      
      if (!subMenu || !parentLink) {
        return;
      }

      // Add ARIA attributes for accessibility
      parentLink.setAttribute('aria-haspopup', 'true');
      parentLink.setAttribute('aria-expanded', 'false');
      subMenu.setAttribute('role', 'menu');
      subMenu.setAttribute('aria-label', `${parentLink.textContent.trim()} submenu`);
      
      // Find all links - handle both regular sub-menus and mega-nav structures
      let subMenuLinks = subMenu.querySelectorAll('a');
      
      if (subMenu.classList.contains('mega-nav')) {
        const megaNavLinks = subMenu.querySelectorAll('.mega-nav__links a, .header__nav-list--nested a');
        if (megaNavLinks.length > 0) {
          subMenuLinks = megaNavLinks;
        }
      }
      
      subMenuLinks.forEach((link) => {
        link.setAttribute('role', 'menuitem');
      });

      // Track if sub-menu is currently open and how it was opened
      let isSubMenuOpen = false;
      let hoverTimeout = null;
      let openedViaKeyboard = false;

      const openSubMenu = (viaKeyboard = false) => {
        if (isSubMenuOpen) return;
        
        // Close any other open sub-menu (especially mega-nav sub-menus)
        if (currentOpenSubMenu && currentOpenSubMenu !== subMenu && currentOpenItem) {
          // Close the previously open sub-menu
          const prevParentLink = currentOpenItem.querySelector('a.title, a');
          if (prevParentLink) {
            prevParentLink.setAttribute('aria-expanded', 'false');
          }
          // If it's a mega-nav, hide it
          if (currentOpenSubMenu.classList.contains('mega-nav')) {
            currentOpenSubMenu.style.display = 'none';
            currentOpenSubMenu.setAttribute('aria-hidden', 'true');
          }
        }
        
        isSubMenuOpen = true;
        openedViaKeyboard = viaKeyboard;
        parentLink.setAttribute('aria-expanded', 'true');
        
        // If it's a mega-nav that was hidden, restore it
        if (subMenu.classList.contains('mega-nav')) {
          subMenu.style.display = '';
          subMenu.removeAttribute('aria-hidden');
        }
        
        currentOpenSubMenu = subMenu;
        currentOpenItem = item;
        
        // Focus first link in sub-menu after a brief delay to allow CSS transition
        hoverTimeout = setTimeout(() => {
          const firstLink = subMenu.querySelector('a[role="menuitem"]');
          if (firstLink && viaKeyboard && document.activeElement !== firstLink) {
            firstLink.focus();
          }
        }, 100);
      };

      const closeSubMenu = (force = false) => {
        if (!isSubMenuOpen) return;
        
        // Don't auto-close if opened via keyboard (unless forced)
        if (openedViaKeyboard && !force) return;
        
        isSubMenuOpen = false;
        openedViaKeyboard = false;
        parentLink.setAttribute('aria-expanded', 'false');
        
        if (currentOpenSubMenu === subMenu) {
          currentOpenSubMenu = null;
          currentOpenItem = null;
        }
        
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }
      };

      item.addEventListener('mouseenter', () => {
        if (!openedViaKeyboard) {
          openSubMenu(false);
        }
      });

      item.addEventListener('mouseleave', (e) => {
        if (!item.contains(e.relatedTarget) && !openedViaKeyboard) {
          closeSubMenu();
          if (subMenu.contains(document.activeElement)) {
            parentLink.focus();
          }
        }
      });

      subMenu.addEventListener('mouseenter', () => {
        if (!openedViaKeyboard) {
          openSubMenu(false);
        }
      });

      subMenu.addEventListener('mouseleave', (e) => {
        if (!item.contains(e.relatedTarget) && !openedViaKeyboard) {
          closeSubMenu();
        }
      });

      let isKeyboardInteraction = false;
      
      parentLink.addEventListener('click', (e) => {
        if (isKeyboardInteraction) {
          e.preventDefault();
          e.stopPropagation();
          isKeyboardInteraction = false;
          return false;
        }
      }, true);

      parentLink.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          isKeyboardInteraction = true;
          setTimeout(() => {
            isKeyboardInteraction = false;
          }, 100);
          
          if (isSubMenuOpen) {
            closeSubMenu(true);
          } else {
            openSubMenu(true);
          }
          return false;
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          e.stopPropagation();
          if (!isSubMenuOpen) {
            openSubMenu(true);
          } else {
            const firstLink = subMenu.querySelector('a[role="menuitem"]');
            if (firstLink) {
              firstLink.focus();
            }
          }
        } else if (e.key === 'Escape' && isSubMenuOpen) {
          e.preventDefault();
          e.stopPropagation();
          closeSubMenu(true);
        }
      });

      parentLink.addEventListener('blur', (e) => {
        setTimeout(() => {
          const newFocusTarget = document.activeElement;
          if (!subMenu.contains(newFocusTarget) && !item.contains(newFocusTarget)) {
            closeSubMenu(true);
          }
        }, 100);
      });

      parentLink.addEventListener('focus', () => {
        if (currentOpenSubMenu && currentOpenSubMenu !== subMenu && currentOpenItem) {
          const prevParentLink = currentOpenItem.querySelector('a.title, a');
          if (prevParentLink) {
            prevParentLink.setAttribute('aria-expanded', 'false');
          }
          if (currentOpenSubMenu.classList.contains('mega-nav')) {
            currentOpenSubMenu.style.display = 'none';
            currentOpenSubMenu.setAttribute('aria-hidden', 'true');
          }
          currentOpenSubMenu = null;
          currentOpenItem = null;
        }
      });

      subMenuLinks.forEach((link, index) => {
        link.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextIndex = (index + 1) % subMenuLinks.length;
            subMenuLinks[nextIndex].focus();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prevIndex = index === 0 ? subMenuLinks.length - 1 : index - 1;
            subMenuLinks[prevIndex].focus();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            closeSubMenu(true);
            parentLink.focus();
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            closeSubMenu(true);
            parentLink.focus();
          } else if (e.key === 'Home') {
            e.preventDefault();
            subMenuLinks[0].focus();
          } else if (e.key === 'End') {
            e.preventDefault();
            subMenuLinks[subMenuLinks.length - 1].focus();
          }
        });

        link.addEventListener('blur', (e) => {
          setTimeout(() => {
            const newFocusTarget = document.activeElement;
            if (!item.contains(newFocusTarget)) {
              closeSubMenu(true);
            }
          }, 100);
        });
      });
    });
  }

  initNestedSubMenuFocus() {
    const topBar = this.querySelector('#top-bar');
    if (!topBar) {
      return;
    }
    
    const nestedSubMenus = topBar.querySelectorAll('.header__nav-list-nested');
    if (!nestedSubMenus || nestedSubMenus.length === 0) {
      return;
    }

    nestedSubMenus.forEach(nestedSubMenu => {
      const parentItem = nestedSubMenu.closest('li');
      if (!parentItem) return;

      const parentLink = parentItem.querySelector('a.title, a');
      if (!parentLink) return;

      const nestedLinks = nestedSubMenu.querySelectorAll('a');
      if (nestedLinks.length === 0) return;

      parentLink.setAttribute('aria-haspopup', 'true');
      parentLink.setAttribute('aria-expanded', 'false');
      nestedSubMenu.setAttribute('role', 'menu');
      nestedSubMenu.setAttribute('aria-label', `${parentLink.textContent.trim()} submenu`);

      nestedLinks.forEach((link) => {
        link.setAttribute('role', 'menuitem');
      });

      let isNestedSubMenuOpen = false;
      let openedViaKeyboard = false;
      let isKeyboardInteraction = false;

      const openNestedSubMenu = (viaKeyboard = false) => {
        if (isNestedSubMenuOpen) return;
        
        isNestedSubMenuOpen = true;
        openedViaKeyboard = viaKeyboard;
        parentLink.setAttribute('aria-expanded', 'true');
        
        if (nestedSubMenu.style.display === 'none') {
          nestedSubMenu.style.display = '';
          nestedSubMenu.removeAttribute('aria-hidden');
        }

        if (viaKeyboard) {
          setTimeout(() => {
            const firstLink = nestedSubMenu.querySelector('a[role="menuitem"]');
            if (firstLink && document.activeElement !== firstLink) {
              firstLink.focus();
            }
          }, 100);
        }
      };

      const closeNestedSubMenu = (force = false) => {
        if (!isNestedSubMenuOpen) return;
        
        if (openedViaKeyboard && !force) return;
        
        isNestedSubMenuOpen = false;
        openedViaKeyboard = false;
        parentLink.setAttribute('aria-expanded', 'false');
      };

      parentItem.addEventListener('mouseenter', () => {
        if (!openedViaKeyboard) {
          openNestedSubMenu(false);
        }
      });

      parentItem.addEventListener('mouseleave', (e) => {
        if (!parentItem.contains(e.relatedTarget) && !openedViaKeyboard) {
          closeNestedSubMenu();
          if (nestedSubMenu.contains(document.activeElement)) {
            parentLink.focus();
          }
        }
      });

      nestedSubMenu.addEventListener('mouseenter', () => {
        if (!openedViaKeyboard) {
          openNestedSubMenu(false);
        }
      });

      nestedSubMenu.addEventListener('mouseleave', (e) => {
        if (!parentItem.contains(e.relatedTarget) && !openedViaKeyboard) {
          closeNestedSubMenu();
        }
      });

      parentLink.addEventListener('click', (e) => {
        if (isKeyboardInteraction) {
          e.preventDefault();
          e.stopPropagation();
          isKeyboardInteraction = false;
          return false;
        }
      }, true);

      parentLink.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          isKeyboardInteraction = true;
          setTimeout(() => {
            isKeyboardInteraction = false;
          }, 100);
          
          if (isNestedSubMenuOpen) {
            closeNestedSubMenu(true);
          } else {
            openNestedSubMenu(true);
          }
          return false;
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          e.stopPropagation();
          if (!isNestedSubMenuOpen) {
            openNestedSubMenu(true);
          } else {
            const firstLink = nestedSubMenu.querySelector('a[role="menuitem"]');
            if (firstLink) {
              firstLink.focus();
            }
          }
        } else if (e.key === 'Escape' && isNestedSubMenuOpen) {
          e.preventDefault();
          e.stopPropagation();
          closeNestedSubMenu(true);
        }
      });

      parentLink.addEventListener('blur', (e) => {
        setTimeout(() => {
          const newFocusTarget = document.activeElement;
          if (!nestedSubMenu.contains(newFocusTarget) && !parentItem.contains(newFocusTarget)) {
            closeNestedSubMenu(true);
          }
        }, 100);
      });

      nestedLinks.forEach((link, index) => {
        link.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextIndex = (index + 1) % nestedLinks.length;
            nestedLinks[nextIndex].focus();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prevIndex = index === 0 ? nestedLinks.length - 1 : index - 1;
            nestedLinks[prevIndex].focus();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            closeNestedSubMenu(true);
            parentLink.focus();
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            closeNestedSubMenu(true);
            parentLink.focus();
          } else if (e.key === 'Home') {
            e.preventDefault();
            nestedLinks[0].focus();
          } else if (e.key === 'End') {
            e.preventDefault();
            nestedLinks[nestedLinks.length - 1].focus();
          }
        });

        link.addEventListener('blur', (e) => {
          setTimeout(() => {
            const newFocusTarget = document.activeElement;
            if (!parentItem.contains(newFocusTarget)) {
              closeNestedSubMenu(true);
            }
          }, 100);
        });
      });
    });
  }

  updateHeaderDimensions() {
    const header = document.querySelector('header-component:not(#fixed-header)') || document.querySelector('.header-wrapper.cf');
    if (!header) return;
    // Temporarily hide sub-menus so they don't inflate the measurement
    const subMenus = header.querySelectorAll('.sub-menu, .header__nav-list-nested');
    const origStyles = [...subMenus].map(el => ({ visibility: el.style.visibility, pointerEvents: el.style.pointerEvents }));
    subMenus.forEach(el => {
      el.style.visibility = 'hidden';
      el.style.pointerEvents = 'none';
    });
    this.headerHeight = header.clientHeight || 0;
    subMenus.forEach((el, i) => {
      el.style.visibility = origStyles[i].visibility || '';
      el.style.pointerEvents = origStyles[i].pointerEvents || '';
    });
    document.documentElement.style.setProperty("--header-height", `${this.headerHeight}px`);
    document.documentElement.style.setProperty("--mobile-header-height", `103px`);
  }  

  handleResize() {
    this.updateHeaderDimensions();
  }

}

class NavigationDrawer extends HTMLElement {
  constructor() {
    super();
  }
  
  connectedCallback() {
    // Move to end of body once constructed
    if (this.parentNode !== document.body) {
      document.body.appendChild(this);
    }
    
    this.addEventListener('click', this.handleNavigation);
    this.addEventListener('click', this.handleMobileTopNav);
    window.addEventListener('resize', this.setHeightVarForPrimaryNavItems.bind(this));
    setTimeout(() => {
      this.setHeightVarForPrimaryNavItems();
    }, 500);
    // Don't overwrite --header-height: the mobile drawer height would be wrong.
    // HeaderComponent handles --header-height; --mobile-header-height is set there.
  }

  updateHeaderDimensions() {
    // NavigationDrawer is the mobile nav panel - its height should NOT be used for --header-height.
    // Only HeaderComponent should update header dimensions.
  }  

  setHeightVarForPrimaryNavItems() {
    document.documentElement.style.setProperty('--header-primary-height', Math.round(document.querySelector('#masthead .header__level-3 nav').clientHeight) + 'px');
  }

  handleNavigation(event) {
    const button = event.target.closest('.item.has-children');
    const backButton = event.target.closest('[data-panel="0"]');

    if (button) {
      if (button.querySelector("a[href='#']")) {
        return false;
      }
      const panelIndex = button.getAttribute('data-connected-panel');
      button.setAttribute('aria-expanded', 'true');
      this.openPanel(panelIndex);
    }

    if (backButton) {
      const parentButton = this.querySelector(`.item.has-children[data-connected-panel="${backButton.getAttribute('data-panel')}"]`);
      if (parentButton) {
        parentButton.setAttribute('aria-expanded', 'false');
      }
      this.resetNavigation();
    }
  }

  handleMobileTopNav(event) {
    const toggleButton = event.target.closest('.mobile-top-nav-toggle');
    const backButton = event.target.closest('.mobile-top-nav-back');
    const shopButton = event.target.closest('.mobile-top-nav-toggle[data-shop-toggle]');
    const mobilePanel = this.querySelector('.mobile-nav__panel');

    if (shopButton) {
      event.preventDefault();
      const level2Nav = this.querySelector('.mobile-nav__level-2 nav');
      const mainNav = this.querySelector('[data-mobile-nav-content="main"]');
      const allTopNavs = this.querySelectorAll('[data-mobile-nav-content^="top-nav-"]');

      if (level2Nav && mainNav && mobilePanel) {
        mobilePanel.classList.add('no-transition');
        allTopNavs.forEach(nav => nav.setAttribute('hidden', ''));
        mainNav.removeAttribute('hidden');
        
        shopButton.setAttribute('aria-expanded', 'true');
        this.querySelectorAll('.mobile-top-nav-toggle').forEach(btn => {
          if (btn !== shopButton) {
            btn.setAttribute('aria-expanded', 'false');
          }
        });
      }
      return;
    }

    if (toggleButton) {
      event.preventDefault();
      const navId = toggleButton.getAttribute('data-top-nav-id');
      const level2Nav = this.querySelector('.mobile-nav__level-2 nav');
      const mainNav = this.querySelector('[data-mobile-nav-content="main"]');
      const targetNav = this.querySelector(`[data-mobile-nav-content="${navId}"]`);

      if (level2Nav && mainNav && targetNav && mobilePanel) {
        mobilePanel.classList.add('no-transition');
        mainNav.setAttribute('hidden', '');
        targetNav.removeAttribute('hidden');
        toggleButton.setAttribute('aria-expanded', 'true');
        
        this.querySelectorAll('.mobile-top-nav-toggle').forEach(btn => {
          if (btn !== toggleButton) {
            btn.setAttribute('aria-expanded', 'false');
          }
        });
      }
      return;
    }

    if (backButton && backButton.getAttribute('data-back-to-main') === 'true') {
      event.preventDefault();
      const level2Nav = this.querySelector('.mobile-nav__level-2 nav');
      const mainNav = this.querySelector('[data-mobile-nav-content="main"]');
      const allTopNavs = this.querySelectorAll('[data-mobile-nav-content^="top-nav-"]');

      if (level2Nav && mainNav && mobilePanel) {
        mobilePanel.classList.add('no-transition');
        allTopNavs.forEach(nav => nav.setAttribute('hidden', ''));
        mainNav.removeAttribute('hidden');
        
        this.querySelectorAll('.mobile-top-nav-toggle').forEach(btn => {
          btn.setAttribute('aria-expanded', 'false');
        });
      }
    }
  }

  openPanel(panelIndex) {
    const panelListWrapper = this.querySelector('.mobile-nav__panel-list-wrapper');
    const panelWrappers = this.querySelectorAll('.mobile-nav__panel-wrapper');
    const mobilePanels = this.querySelectorAll('.mobile-nav__panel');

    panelWrappers.forEach(wrapper => {
      wrapper.setAttribute('hidden', '');
    });

    mobilePanels.forEach(panel => {
      panel.classList.remove('transitioned-behind', 'transitioned-top', 'no-transition');
      panelListWrapper.style.removeProperty('height');
      panel.style.removeProperty('height');
    });
    
    const targetPanelWrapper = this.querySelector(`[data-level-2-panel="${panelIndex}"]`);
    if (targetPanelWrapper) {
      targetPanelWrapper.removeAttribute('hidden');
      setTimeout(() => {
        panelListWrapper.style.height = `${targetPanelWrapper.offsetHeight + 40}px`;
      }, 200);
    }
    
    if (mobilePanels.length > 0) {
      const firstPanel = mobilePanels[0];
      const lastPanel = mobilePanels[mobilePanels.length - 1];
      
      firstPanel.classList.add('transitioned-behind');
      firstPanel.style.height = '0';
      
      lastPanel.classList.add('transitioned-top');
    }
  }

  resetNavigation() {
    const panelWrappers = this.querySelectorAll('.mobile-nav__panel-wrapper');
    const mobilePanels = this.querySelectorAll('.mobile-nav__panel');
    const panelListWrapper = this.querySelector('.mobile-nav__panel-list-wrapper');

    panelWrappers.forEach(wrapper => {
      wrapper.setAttribute('hidden', '');
    });

    mobilePanels.forEach(panel => {
      panel.classList.remove('transitioned-behind', 'transitioned-top', 'no-transition');
      panelListWrapper.style.removeProperty('height');
      panel.style.removeProperty('height');
    });

    this.querySelectorAll('.item.has-children').forEach(button => {
      button.setAttribute('aria-expanded', 'false');
    });
  }
}
if (!window.customElements.get('header-component')) {
  window.customElements.define('header-component', HeaderComponent);
}
if (!window.customElements.get('navigation-drawer')) {
  window.customElements.define('navigation-drawer', NavigationDrawer);
}